import { useEffect, useState, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth.store';
import { useBranchStore } from '../../stores/branch.store';
import { useBranches } from '../../api/queries/settings.queries';
import {
  ShoppingCart,
  Monitor,
  Package,
  Heart,
  Trash2,
  ShoppingBag,
  TruckIcon,
  Tag,
  Menu as MenuIcon,
  ChefHat,
  Settings,
  LogOut,
  ChevronDown,
  Building2,
  Beaker,
  ClipboardList,
  User,
  LayoutDashboard,
  FileText,
  BarChart3,
} from 'lucide-react';

function Layout() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { selectedBranchId, selectedBranchName, setSelectedBranch } = useBranchStore();
  const { data: branches } = useBranches();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);

  // Set default branch or validate stored branch exists
  useEffect(() => {
    if (branches && branches.length > 0) {
      // Check if stored branchId is valid (exists in branches list)
      const storedBranchValid = selectedBranchId &&
        branches.some(b => b._id === selectedBranchId);

      if (!storedBranchValid) {
        // Stored branch is invalid or missing - set a valid default
        if (user?.branchId) {
          const userBranch = branches.find(b => b._id === user.branchId);
          if (userBranch) {
            setSelectedBranch(userBranch._id, userBranch.name);
            return;
          }
        }

        // For HQ users or if user branch not found, use first branch
        const firstBranch = branches.find(b => b.type === 'branch');
        if (firstBranch) {
          setSelectedBranch(firstBranch._id, firstBranch.name);
        }
      }
    }
  }, [user, branches, selectedBranchId, setSelectedBranch]);

  const handleLogout = () => {
    logout();
    setSelectedBranch(null, null);
    navigate('/login');
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setShowLangMenu(false);
  };

  const handleBranchChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const branchId = e.target.value;
    const branch = branches?.find(b => b._id === branchId);
    if (branch) {
      setSelectedBranch(branch._id, branch.name);
    }
  };

  const canSelectBranch = user?.role === 'HQ_ADMIN' || user?.role === 'HQ_PURCHASING';

  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'th', label: 'ไทย', flag: '🇹🇭' },
  ];

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  // Categorized navigation
  const navCategories = [
    {
      label: 'Executive',
      items: [
        { path: '/dashboard', label: t('dashboard.title') || 'Dashboard', icon: LayoutDashboard, roles: ['HQ_ADMIN', 'BRANCH_MANAGER'] },
        { path: '/reports', label: t('reports.title') || 'Sales Reports', icon: FileText, roles: ['HQ_ADMIN', 'BRANCH_MANAGER'] },
        { path: '/performance-analytics', label: 'Performance Analytics', icon: BarChart3, roles: ['HQ_ADMIN', 'BRANCH_MANAGER'] },
      ],
    },
    {
      label: 'Operations',
      items: [
        { path: '/orders', label: 'Orders', icon: ClipboardList, roles: ['POS_OPERATOR', 'BRANCH_SUPERVISOR', 'BRANCH_MANAGER', 'HQ_ADMIN'] },
      ],
    },
    {
      label: 'Inventory & Stock',
      items: [
        { path: '/inventory', label: t('inventory.title'), icon: Package, roles: ['BRANCH_MANAGER', 'WAREHOUSE_STAFF', 'HQ_ADMIN'] },
        { path: '/purchasing', label: t('purchasing.title'), icon: ShoppingBag, roles: ['BRANCH_MANAGER', 'HQ_PURCHASING', 'HQ_ADMIN', 'WAREHOUSE_STAFF'] },
        { path: '/transfers', label: 'Transfers', icon: TruckIcon, roles: ['BRANCH_MANAGER', 'WAREHOUSE_STAFF', 'HQ_ADMIN'] },
        { path: '/waste', label: t('waste.title'), icon: Trash2, roles: ['BRANCH_MANAGER', 'HQ_ADMIN'] },
      ],
    },
    {
      label: 'Customers',
      items: [
        { path: '/loyalty', label: t('loyalty.title'), icon: Heart, roles: ['POS_OPERATOR', 'BRANCH_SUPERVISOR', 'BRANCH_MANAGER', 'HQ_ADMIN'] },
        { path: '/promotions', label: 'Promotions', icon: Tag, roles: ['HQ_ADMIN'] },
      ],
    },
    {
      label: 'Menu',
      items: [
        { path: '/menu-management', label: t('menu.management'), icon: MenuIcon, roles: ['HQ_ADMIN'] },
        { path: '/ingredients', label: 'Ingredients', icon: Beaker, roles: ['HQ_ADMIN'] },
        { path: '/recipe-management', label: 'Recipes', icon: ChefHat, roles: ['HQ_ADMIN'] },
      ],
    },
    {
      label: 'System',
      items: [
        { path: '/settings', label: 'Settings', icon: Settings, roles: ['HQ_ADMIN', 'BRANCH_MANAGER'] },
      ],
    },
  ];

  // Filter categories and items based on user role
  const visibleCategories = navCategories
    .map((category) => ({
      ...category,
      items: category.items.filter((item) => user?.role && item.roles.includes(user.role)),
    }))
    .filter((category) => category.items.length > 0);

  // Check role permissions for quick links
  const canAccessPOS = user?.role && ['POS_OPERATOR', 'BRANCH_SUPERVISOR', 'BRANCH_MANAGER', 'HQ_ADMIN'].includes(user.role);
  const canAccessKDS = user?.role && ['POS_OPERATOR', 'BRANCH_SUPERVISOR', 'BRANCH_MANAGER', 'HQ_ADMIN'].includes(user.role);
  const canAccessAdmin = user?.role && ['HQ_ADMIN', 'HQ_PURCHASING', 'BRANCH_MANAGER', 'BRANCH_SUPERVISOR'].includes(user.role);

  // Get selected branch display name
  const selectedBranchDisplay = branches?.find(b => b._id === selectedBranchId);

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-60 border-r bg-card/50 flex flex-col">
        {/* Header */}
        <div className="p-5 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-sm">
              <span className="text-primary-foreground font-bold">NT</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">Nose Tea</h1>
              <p className="text-xs text-muted-foreground">POS System</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-6">
            {visibleCategories.map((category) => (
              <div key={category.label}>
                {/* Category Header */}
                <div className="flex items-center gap-2 px-3 mb-2">
                  <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-widest">
                    {category.label}
                  </span>
                  <div className="flex-1 h-px bg-border/50" />
                </div>
                {/* Category Items */}
                <ul className="space-y-1">
                  {category.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;

                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 text-sm ${
                            isActive
                              ? 'bg-primary text-primary-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                          }`}
                        >
                          <Icon className={`h-4 w-4 transition-transform duration-150 ${!isActive && 'group-hover:scale-110'}`} />
                          <span className="font-medium">{item.label}</span>
                          {isActive && (
                            <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-foreground/70" />
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t bg-muted/30">
          <div className="text-xs text-muted-foreground text-center">
            v1.0.0
          </div>
        </div>
      </aside>

      {/* Main Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header Bar */}
        <header className="h-14 border-b bg-card flex items-center justify-between px-4 shrink-0">
          {/* Left - Empty or can add breadcrumb later */}
          <div className="flex items-center gap-3">
            {/* Placeholder for future breadcrumb */}
          </div>

          {/* Right - Branch, Language & User */}
          <div className="flex items-center gap-3">
            {/* Branch Selector */}
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              {canSelectBranch && branches && branches.length > 0 ? (
                <select
                  value={selectedBranchId || ''}
                  onChange={handleBranchChange}
                  className="px-3 py-1.5 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary min-w-[200px]"
                >
                  {branches.filter(b => b.type === 'branch').map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name} ({branch.code})
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-sm font-medium">
                  {selectedBranchDisplay?.name || selectedBranchName || 'No branch'}
                </span>
              )}
            </div>

            <div className="h-6 border-l border-border" />

            {/* Language Switcher */}
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => {
                  setShowLangMenu(!showLangMenu);
                  setShowUserMenu(false);
                }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-background hover:bg-accent transition-colors"
              >
                <span className="text-base">{currentLang.flag}</span>
                <span className="text-sm font-medium">{currentLang.label}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>

              {showLangMenu && (
                <div className="absolute right-0 mt-2 w-40 rounded-lg border bg-card shadow-lg z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-accent transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        i18n.language === lang.code ? 'bg-accent' : ''
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span className="text-sm">{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* User Menu with Navigation Links */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => {
                  setShowUserMenu(!showUserMenu);
                  setShowLangMenu(false);
                }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-background hover:bg-accent transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground text-xs font-bold">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <span className="text-sm font-medium">{user?.username}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 rounded-lg border bg-card shadow-lg z-50">
                  {/* User Info Header */}
                  <div className="px-4 py-3 border-b">
                    <p className="text-sm font-medium">{user?.username}</p>
                    <p className="text-xs text-muted-foreground">{user?.role?.replace(/_/g, ' ')}</p>
                  </div>

                  {/* Quick Access Links */}
                  <div className="py-1 border-b">
                    {canAccessPOS && (
                      <Link
                        to="/pos"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent transition-colors"
                      >
                        <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                        <span>{t('nav.pos') || 'Point of Sale'}</span>
                      </Link>
                    )}
                    {canAccessKDS && (
                      <Link
                        to="/kds"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent transition-colors"
                      >
                        <Monitor className="h-4 w-4 text-muted-foreground" />
                        <span>{t('nav.kds') || 'Kitchen Display'}</span>
                      </Link>
                    )}
                    {canAccessAdmin && (
                      <Link
                        to="/orders"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent transition-colors bg-accent"
                      >
                        <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
                        <span>{t('nav.adminPanel') || 'Admin Panel'}</span>
                      </Link>
                    )}
                  </div>

                  {/* Profile Link */}
                  <div className="py-1 border-b">
                    <Link
                      to="/profile"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-accent transition-colors"
                    >
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{t('common.profile')}</span>
                    </Link>
                  </div>

                  {/* Logout */}
                  <div className="py-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>{t('common.logout')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* Click outside to close menus */}
      {(showUserMenu || showLangMenu) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowUserMenu(false);
            setShowLangMenu(false);
          }}
        />
      )}
    </div>
  );
}

export default Layout;
